import { useState, useEffect } from 'react';
import { useLogicModel, Input } from '../../../contexts/LogicModelContext';
import { supabase } from '../../../lib/supabase';
import { useTenant } from '../../../hooks/useTenant';

const CATEGORIES = [
  { value: 'funding', label: 'Funding', icon: '💰', color: 'bg-green-100 text-green-800' },
  { value: 'staff', label: 'Staff', icon: '👥', color: 'bg-blue-100 text-blue-800' },
  { value: 'equipment', label: 'Equipment', icon: '🖥️', color: 'bg-purple-100 text-purple-800' },
  { value: 'partnerships', label: 'Partnerships', icon: '🤝', color: 'bg-amber-100 text-amber-800' },
  { value: 'knowledge', label: 'Knowledge', icon: '📚', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'other', label: 'Other', icon: '📦', color: 'bg-gray-100 text-gray-800' },
];

type Category = 'funding' | 'staff' | 'equipment' | 'partnerships' | 'knowledge' | 'other';

export function InputsStep() {
  const { logicModel, updateInputs, markStepComplete } = useLogicModel();
  const { organization } = useTenant();
  const [inputs, setInputs] = useState<Input[]>([]);
  const [activeCategory, setActiveCategory] = useState<Category>('funding');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    quantity: '',
    value: '',
  });

  useEffect(() => {
    if (logicModel?.inputs) {
      setInputs(logicModel.inputs);
    }
  }, [logicModel?.inputs]);

  useEffect(() => {
    if (inputs.length > 0) {
      markStepComplete('inputs');
    }
  }, [inputs.length, markStepComplete]);

  const getInputsByCategory = (category: Category) =>
    inputs.filter(i => i.category === category);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      quantity: '',
      value: '',
    });
    setEditingId(null);
    setIsAdding(false);
  };

  const handleEdit = (input: Input) => {
    setFormData({
      name: input.name,
      description: input.description || '',
      quantity: input.quantity || '',
      value: input.value?.toString() || '',
    });
    setEditingId(input.id || null);
    setIsAdding(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Input name is required');
      return;
    }

    if (!logicModel?.id || !organization) return;

    setSaving(true);
    setError(null);

    try {
      const inputData = {
        category: activeCategory,
        name: formData.name,
        description: formData.description || null,
        quantity: formData.quantity || null,
        value: formData.value ? parseFloat(formData.value) : null,
      };

      if (editingId) {
        const { error: updateError } = await supabase
          .from('inputs')
          .update(inputData)
          .eq('id', editingId);

        if (updateError) throw updateError;

        const updated = inputs.map(i =>
          i.id === editingId ? { ...i, ...inputData } : i
        );
        setInputs(updated);
        updateInputs(updated);
      } else {
        const { data: newInput, error: insertError } = await supabase
          .from('inputs')
          .insert({
            logic_model_id: logicModel.id,
            organization_id: organization.organization_id,
            ...inputData,
            sort_order: getInputsByCategory(activeCategory).length,
          })
          .select()
          .single();

        if (insertError) throw insertError;

        const newInputs = [...inputs, newInput];
        setInputs(newInputs);
        updateInputs(newInputs);
      }

      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (inputId: string) => {
    if (!confirm('Delete this input?')) return;

    try {
      const { error: deleteError } = await supabase
        .from('inputs')
        .delete()
        .eq('id', inputId);

      if (deleteError) throw deleteError;

      const updated = inputs.filter(i => i.id !== inputId);
      setInputs(updated);
      updateInputs(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const formatValue = (value: number | null | undefined) => {
    if (value === null || value === undefined) return null;
    return `$${value.toLocaleString()}`;
  };

  const getCategoryInfo = (category: string) => 
    CATEGORIES.find(c => c.value === category) || CATEGORIES[5];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          📦 Define Inputs / Resources
        </h2>
        <p className="text-gray-600">
          Inputs are what you invest to make your activities possible. They're the foundation that constrains what's achievable.
        </p>
      </div>

      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <h3 className="font-medium text-orange-900 mb-2">📁 Input Categories</h3>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {CATEGORIES.map((cat) => (
            <span key={cat.value} className="text-sm text-orange-800">
              {cat.icon} {cat.label}
            </span>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Category Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px space-x-1 overflow-x-auto">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setActiveCategory(cat.value as Category)}
              className={`py-2 px-3 border-b-2 text-sm font-medium whitespace-nowrap ${
                activeCategory === cat.value
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {cat.icon} {cat.label}
              <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-gray-100">
                {getInputsByCategory(cat.value as Category).length}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Inputs List for Active Category */}
      <div className="space-y-3">
        {getInputsByCategory(activeCategory).length > 0 ? (
          getInputsByCategory(activeCategory).map((input) => {
            const catInfo = getCategoryInfo(input.category);
            return (
              <div
                key={input.id}
                className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${catInfo.color}`}>
                        {catInfo.icon} {catInfo.label}
                      </span>
                      <h4 className="font-medium text-gray-900">{input.name}</h4>
                    </div>
                    {input.description && (
                      <p className="text-sm text-gray-600 mt-1">{input.description}</p>
                    )}
                    <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-500">
                      {input.quantity && (
                        <span>📊 {input.quantity}</span>
                      )}
                      {input.value !== null && input.value !== undefined && (
                        <span>💵 {formatValue(input.value)}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handleEdit(input)}
                      className="text-gray-400 hover:text-blue-600"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => input.id && handleDelete(input.id)}
                      className="text-gray-400 hover:text-red-600"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <p className="text-center text-gray-500 py-4">
            No {getCategoryInfo(activeCategory).label.toLowerCase()} inputs yet
          </p>
        )}
      </div>

      {/* Add/Edit Form */}
      {isAdding ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
          <h3 className="font-medium text-gray-900">
            {editingId ? 'Edit Input' : `Add ${getCategoryInfo(activeCategory).label} Input`}
          </h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Input Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
              placeholder={activeCategory === 'funding' ? 'e.g., Grant from XYZ Foundation' : 
                          activeCategory === 'staff' ? 'e.g., Program Coordinator' :
                          activeCategory === 'equipment' ? 'e.g., Training laptops' :
                          activeCategory === 'partnerships' ? 'e.g., Local employer network' :
                          activeCategory === 'knowledge' ? 'e.g., Industry curriculum' :
                          'e.g., Resource name'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
              placeholder="Additional details about this resource"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantity
              </label>
              <input
                type="text"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                placeholder={activeCategory === 'staff' ? 'e.g., 2 FTE' : 
                            activeCategory === 'equipment' ? 'e.g., 25 units' : 
                            'e.g., amount'}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Value ($)
              </label>
              <input
                type="number"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                placeholder="Dollar value if applicable"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={resetForm}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md disabled:opacity-50"
            >
              {saving ? 'Saving...' : editingId ? 'Update' : 'Add Input'}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsAdding(true)}
          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-orange-400 hover:text-orange-600 transition-colors"
        >
          + Add {getCategoryInfo(activeCategory).label} Input
        </button>
      )}

      {/* Example Inputs */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-3">📋 Example Inputs</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
          <span>💰 $180,000 annual budget</span>
          <span>👥 2 FTE instructors</span>
          <span>🖥️ 25 laptops for training</span>
          <span>🤝 Local employer network partnership</span>
          <span>📚 Curriculum from industry partners</span>
          <span>👥 5 volunteer mentors</span>
        </div>
      </div>
    </div>
  );
}
